import React, { useEffect, useRef } from "react";
import { AwardIcon, CalendarIcon, CheckCircleIcon, ClockIcon, MessageCircleIcon, MonitorIcon, TrendingUpIcon, VideoIcon } from "lucide-react";
import { InterviewVideo } from "@type/videoInterview";
import { formatDuration, parseFormat } from "@util/date";
import { interviewStatusAborted, interviewStatusComplete, interviewStatusInComplete, interviewStatusNone, interviewStatusPartial, interviewStatusPendingInterview, interviewStatusPendingVerification } from "@constant/app";
import { CandidateInterviewStatus } from "@type/candidate";
import { useUserProfileContext } from "@context/user_profile";
import ourteamThumbnail from "@asset/ourteam_thumbnail.png";
import { cn } from "@util/tailwind";
import { useHlsVideo } from "@hook/useHlsVideo";
import { LoaderIcon } from "lucide-react";
import { AudioRecordingPlayer } from "@component/audio-recording-player";

interface Props {
    isHide?: boolean;
    isLoadingVideo: boolean;
    interviewStatus: CandidateInterviewStatus | undefined;
    s3VideoLink: string | null;
    videoInterview: InterviewVideo;
    setVideoDuration: (newDuration: number) => void
}

export const InterviewPlayer = (props: Props) => {
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [interviewDuration, _setInterviewDuration] = React.useState<number | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Prefer the streaming `video_url` (BE #3092) over the legacy download
    // `s3_link`; fall back through older prop shapes (matches the detail page)
    // so legacy payloads still play while the BE rolls out.
    const videoSrc =
        props.videoInterview?.video_url ??
        props.videoInterview?.s3_link ??
        props.s3VideoLink ??
        null;

    // Audio-only recording: the resolved source is a plain audio file (e.g. an
    // `.mp3` stored at `s3_link`). Play it in a real <audio> player instead of a
    // <video> element, which would render a blank frame with no proper audio UI.
    // The regex tolerates a presigned-URL query string (`.mp3?X-Amz-...`).
    const isAudioSrc = !!videoSrc && /\.(mp3|m4a|aac|wav|ogg)(\?|#|$)/i.test(videoSrc);

    // Stream the recording: HLS via hls.js / native, or progressive MP4 via the
    // element. `buffering` drives the loading overlay below. Skip HLS entirely for
    // an audio source (there's no <video> element to attach it to).
    const { buffering } = useHlsVideo(videoRef, isAudioSrc ? null : videoSrc);

    // v2 (ElevenLabs) playback. PREFERRED path: the BE muxes the candidate video + the
    // EL conversation audio into ONE seekable file (`is_combined`), served as `s3_link`
    // with sound baked in — we play it as a normal `<video>` (seeking just works, no
    // drift). TRANSITIONAL path (before the combined file lands, ~15-90s after the
    // call): the recorded video is candidate-camera only (mic-only / silent) and the
    // AI voice lives in a separate `conversation_audio_url`; we play the video MUTED
    // with that audio as the soundtrack (its controls drive the muted video).
    const audioRef = useRef<HTMLAudioElement>(null);
    const isElVideo = !!props.videoInterview?.is_video_el;
    const isCombined = !!props.videoInterview?.is_combined;
    const conversationAudioUrl = props.videoInterview?.conversation_audio_url ?? null;
    const audioOffsetMs = props.videoInterview?.audio_offset_ms ?? 0;
    // The dual-track (muted video + separate audio) path applies ONLY on the
    // transitional case — never when the combined file is ready (then `s3_link` already
    // has the sound and `conversation_audio_url` is null; it plays as a normal video).
    const useElAudio = isElVideo && !!conversationAudioUrl && !isCombined;
    // EL playback is a SINGLE player: when there's a video, it's the control surface
    // and the EL audio is its hidden soundtrack. With no video (upload failed → audio
    // only), the EL audio element shows its own controls as the standalone player.
    const elAudioStandalone = useElAudio && !videoSrc;

    // The recording player heading reflects the medium — "Audio" whenever what
    // actually renders is an audio player (an mp3-only source, or the standalone
    // EL conversation-audio fallback), "Video" otherwise.
    const recordingHeading = (isAudioSrc || elAudioStandalone)
        ? "Interview Audio Recording"
        : "Interview Video Recording";

    // A v2 (EL) recording is only reviewable once either the combined file (`is_combined`)
    // or the separate `conversation_audio_url` (both voices) is ready. Per the API
    // contract those can be null for a short while after the call ends. When the
    // interview is otherwise complete/partial but NEITHER is ready yet, we must NOT fall
    // back to the legacy player — that would play the silent, candidate-only camera webm
    // and read as a successful (but mute) review. Show an explicit "audio still preparing"
    // state instead and let HR refresh. (no-fallback)
    const isReviewableStatus = [interviewStatusComplete, interviewStatusPartial].includes(
        props.interviewStatus?.status || "",
    );
    // When the source itself is a ready audio file (an mp3 at `s3_link`), the
    // recording IS available — ignore `is_combined`/`conversation_audio_url` and
    // don't show the "audio still preparing" state; render the audio player below.
    const elAudioPending = isElVideo && isReviewableStatus && !isCombined && !conversationAudioUrl && !isAudioSrc;

    // Single-player sync: the EL conversation audio (both voices) is the CONTROL
    // surface — its <audio controls> bar is laid out flush under the plain (controls-
    // less) video so the two read as one component. The AUDIO drives transport; the
    // video is MUTED (its candidate-mic-only track would double the EL audio; muting
    // silences only the audio — the FRAMES still render) and just follows the audio's
    // play/pause/seek/rate. Re-seek the video only when drift exceeds a threshold so a
    // header-less webm isn't reseeked every tick. (No video → audio is standalone; this
    // short-circuits and the audio shows its own controls.)
    useEffect(() => {
        const video = videoRef.current;
        const audio = audioRef.current;
        if (!video) return;

        // When NOT on the dual-track EL path (combined file, v1, or audio not ready),
        // make sure the video is a normal UNMUTED player. The `muted` prop alone is
        // unreliable through React (it only sets the DOM property on mount), so the
        // muted state set below for the transitional path would otherwise leak across a
        // re-render into combined playback and play the muxed file silently. (Jack review)
        if (!useElAudio) {
            video.muted = false;
            return;
        }
        if (!audio) return;
        const offsetSec = audioOffsetMs / 1000;

        // The video carries no controls and must stay muted (it follows the audio).
        // Remember the prior state so cleanup can restore it across mode changes.
        const previousMuted = video.muted;
        video.muted = true;

        const syncVideoToAudio = () => {
            const target = Math.max(0, audio.currentTime - offsetSec);
            if (Math.abs(video.currentTime - target) > 0.3) {
                video.currentTime = target;
            }
        };

        const onPlay = () => { syncVideoToAudio(); video.playbackRate = audio.playbackRate; video.play().catch(() => {}); };
        const onPause = () => { video.pause(); };
        const onSeeking = () => { syncVideoToAudio(); };
        const onTimeUpdate = () => { syncVideoToAudio(); };
        const onRateChange = () => { video.playbackRate = audio.playbackRate; };
        const onLoadedMetadata = () => { syncVideoToAudio(); };
        // When the AUDIO buffers/stalls on a slow connection, PAUSE the video too —
        // otherwise it keeps playing from the last play() and runs ahead of the muted
        // soundtrack, breaking lip-sync (reviewer sees the wrong moment). Resume + snap
        // back in sync when the audio recovers; pause + snap on end. (Jack review)
        const onWaiting = () => { video.pause(); };
        const onStalled = () => { video.pause(); };
        const onPlaying = () => { syncVideoToAudio(); video.play().catch(() => {}); };
        const onEnded = () => { syncVideoToAudio(); video.pause(); };

        audio.addEventListener("play", onPlay);
        audio.addEventListener("pause", onPause);
        audio.addEventListener("seeking", onSeeking);
        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("ratechange", onRateChange);
        audio.addEventListener("waiting", onWaiting);
        audio.addEventListener("stalled", onStalled);
        audio.addEventListener("playing", onPlaying);
        audio.addEventListener("ended", onEnded);
        video.addEventListener("loadedmetadata", onLoadedMetadata);
        return () => {
            audio.removeEventListener("play", onPlay);
            audio.removeEventListener("pause", onPause);
            audio.removeEventListener("seeking", onSeeking);
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("ratechange", onRateChange);
            audio.removeEventListener("waiting", onWaiting);
            audio.removeEventListener("stalled", onStalled);
            audio.removeEventListener("playing", onPlaying);
            audio.removeEventListener("ended", onEnded);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            // Restore the pre-transitional mute state so leaving the dual-track path
            // (→ combined / v1) never leaves the reused <video> stuck muted.
            video.muted = previousMuted;
        };
    }, [useElAudio, audioOffsetMs, videoSrc, conversationAudioUrl]);

    useEffect(() => {
        if (interviewDuration) {
            props.setVideoDuration(interviewDuration);
        }
    }, [interviewDuration]);

    useEffect(() => {
        // ElevenLabs (v2) recordings: the BE supplies an authoritative
        // `duration_video` (seconds). Prefer it for the DISPLAYED duration — their
        // locally-recorded webm has no Duration in its header (native duration ===
        // Infinity). Only trust it when it's a finite positive number.
        const beDuration = props.videoInterview?.duration_video;
        const hasBeDuration =
            !!props.videoInterview?.is_video_el &&
            typeof beDuration === "number" &&
            Number.isFinite(beDuration) &&
            beDuration > 0;
        if (hasBeDuration) {
            _setInterviewDuration(beDuration as number);
            return;
        }

        // For an audio source the AudioRecordingPlayer reports its own duration via
        // onDurationChange, so nothing to read here. For video, read from the element.
        if (isAudioSrc) return;
        const mediaElement: HTMLMediaElement | null = videoRef.current;
        if (!videoSrc || !mediaElement) return;

        // No BE duration → read it from the element's metadata. We deliberately do
        // NOT force a header-less webm's duration by seeking to EOF: that scans the
        // whole file before the player is usable, which made long recordings take a
        // very long time to load. We just take whatever finite native duration the
        // browser reports.
        const handleLoadedMetadata = () => {
            if (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0) {
                _setInterviewDuration(mediaElement.duration);
            }
        };
        if (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0) {
            _setInterviewDuration(mediaElement.duration);
        }
        mediaElement.addEventListener("loadedmetadata", handleLoadedMetadata);
        return () => {
            mediaElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
        };
    }, [videoSrc, isAudioSrc, props.videoInterview?.is_video_el, props.videoInterview?.duration_video]);

    // EL audio still being prepared by the BE → explicit pending state (never the
    // silent legacy-video fallback). HR can refresh once the poller finishes.
    if (elAudioPending) {
        return (
            <div className={`border border-gray-200 dark:border-gray-700 rounded-md p-4 ${props.isHide ? "hidden" : ""}`}>
                <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                    <MonitorIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                    {recordingHeading}
                </h4>
                <div className="flex min-h-[220px] items-center justify-center rounded-md bg-gray-50 dark:bg-gray-800 text-center">
                    <div className="max-w-md space-y-2 px-4">
                        <LoaderIcon className="h-6 w-6 animate-spin text-blue-500 mx-auto mb-1" />
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            Interview recording is still being prepared.
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Please refresh in a moment to review the full recording.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`border border-gray-200 dark:border-gray-700 rounded-md p-4 ${props.isHide ? "hidden" : ""}`}>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                <MonitorIcon className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                {recordingHeading}
            </h4>
            <div className={cn("mb-4", !isAudioSrc && "rounded-3xl overflow-hidden outline outline-1 outline-gray-200 dark:outline-gray-700")}>
                <div className={cn("w-full relative", !isAudioSrc && "aspect-video", {
                    "hidden": (![interviewStatusComplete, interviewStatusPartial].includes(props.interviewStatus?.status || "") || !videoSrc)
                })}>
                    {videoSrc && (isAudioSrc ? (
                        <AudioRecordingPlayer
                            src={videoSrc}
                            onDurationChange={_setInterviewDuration}
                        />
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                id="interview-video"
                                className={"w-full h-full"}
                                // Only the TRANSITIONAL EL path (`useElAudio`) plays the video
                                // PLAIN + MUTED with the merged EL <audio> bar below as the
                                // control surface (the sync effect slaves the muted video to
                                // it). The combined-file EL path, and v1, are a normal video:
                                // native controls + sound (the combined file has audio baked in).
                                controls={!useElAudio}
                                muted={useElAudio}
                                controlsList="nodownload"
                                preload="metadata"
                                playsInline
                                onContextMenu={(e) => e.preventDefault()}
                                poster={ourteamThumbnail}
                            >
                                Your browser does not support the video tag.
                            </video>
                            {buffering && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                                    <LoaderIcon className="h-8 w-8 animate-spin text-white" />
                                </div>
                            )}
                        </>
                    ))}
                </div>
                {/* EL control bar — the <audio> controls laid out flush under the plain
                    video, inside the same rounded container, so they read as one player.
                    The audio is the control surface; the muted video above follows it. */}
                {useElAudio && conversationAudioUrl && videoSrc && isReviewableStatus && (
                    <div className="bg-gray-900 px-3 py-2">
                        <audio
                            ref={audioRef}
                            src={conversationAudioUrl}
                            controls
                            controlsList="nodownload"
                            preload="metadata"
                            className="w-full block"
                        />
                    </div>
                )}
                <div className={cn("aspect-video w-full relative bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center text-center py-4", {
                    "hidden": [interviewStatusComplete, interviewStatusPartial].includes(props.interviewStatus?.status || "")
                })}>
                    <div
                        className={cn("rounded-full p-6 inline-flex mb-4 sm:mt-0 mt-4", {
                            "bg-blue-50 ": props.interviewStatus?.status !== interviewStatusInComplete,
                            "bg-orange-50": props.interviewStatus?.status === interviewStatusInComplete || props.interviewStatus?.status === interviewStatusAborted,
                            "border border-orange-400": props.interviewStatus?.status === interviewStatusInComplete || props.interviewStatus?.status === interviewStatusAborted
                        })}
                    >
                        <VideoIcon className={cn("h-10 w-10", {
                            "text-blue-600": props.interviewStatus?.status !== interviewStatusInComplete,
                            "text-orange-600": props.interviewStatus?.status === interviewStatusInComplete || props.interviewStatus?.status === interviewStatusAborted,
                        })} />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-3">
                        {(props.interviewStatus?.status === interviewStatusInComplete) ? "Incomplete Interview" : props.interviewStatus?.status === interviewStatusAborted ? "Aborted Interview" : "AI Interview Pending"}
                    </h4>
                    {!isLoadingUserProfile && userInfo?.role === "job_seeker" && (
                        <>
                            <div className="px-4 sm:px-0">
                                <p className="text-gray-600 text-sm sm:text-base text-center mb-2">
                                    {props.interviewStatus?.status === interviewStatusPendingInterview && !props.interviewStatus?.reason ?
                                        "You may proceed to retake your AI Interview." :
                                        props.interviewStatus?.status === interviewStatusPendingInterview ?
                                            "Complete your AI interview to stand out to employers." :
                                            (props.interviewStatus?.status === interviewStatusNone || props.interviewStatus?.status === interviewStatusPendingVerification) &&
                                            "You have submitted a retake for your AI Interview."}
                                </p>
                                <p className="text-gray-600 text-sm sm:text-base mb-6 sm:whitespace-nowrap">
                                    It takes just 20 minutes to significantly increase your chances of
                                    getting hired.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6 px-6 sm:px-0 sm:max-w-2xl mx-auto">
                                <div className="bg-white p-3 rounded-lg shadow-md border border-gray-100 h-24 flex flex-col items-center justify-center">
                                    <TrendingUpIcon className="h-5 w-5 text-blue-500 mb-2 mx-auto" />

                                    <p className="text-xs text-gray-600 px-1">
                                        Boost your profile visibility
                                    </p>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-md border border-gray-100 h-24 flex flex-col items-center justify-center">
                                    <AwardIcon className="h-5 w-5 text-blue-500 mb-2 mx-auto" />

                                    <p className="text-xs text-gray-600 px-1">
                                        Highlight key skills &amp; achievements
                                    </p>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-md border border-gray-100 h-24 flex flex-col items-center justify-center">
                                    <CheckCircleIcon className="h-5 w-5 text-blue-500 mb-2 mx-auto" />

                                    <p className="text-xs text-gray-600 px-1">
                                        Get matched with top jobs
                                    </p>
                                </div>
                                <div className="bg-white p-3 rounded-lg shadow-md border border-gray-100 h-24 flex flex-col items-center justify-center">
                                    <MessageCircleIcon className="h-5 w-5 text-blue-500 mb-2 mx-auto" />

                                    <p className="text-xs text-gray-600 px-1">
                                        Natural & friendly AI conversation
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {!isLoadingUserProfile && userInfo?.role === "hr" && (
                        <>
                            <p className="text-gray-500 dark:text-gray-500 text-sm text-center mt-2 sm:block hidden">
                                {props.interviewStatus?.status === interviewStatusAborted ? (
                                    <>
                                        Candidate aborted the interview at {interviewDuration && interviewDuration < 60 ? `${Math.floor(interviewDuration)} seconds` : formatDuration(interviewDuration || 0)},<br />which does not provide enough meaningful information for review.
                                    </>
                                ) : "The candidate has not completed the AI Interview yet."}
                            </p>
                            <p className="text-gray-500 dark:text-gray-500 text-sm text-center mt-2 sm:hidden block">
                                {props.interviewStatus?.status === interviewStatusAborted ? (
                                    <>
                                        Candidate aborted the interview at {interviewDuration && interviewDuration < 60 ? `${Math.floor(interviewDuration)} seconds` : formatDuration(interviewDuration || 0)},<br />which does not provide enough meaningful information for review.
                                    </>
                                ) : (
                                    <>
                                        The candidate has not completed <br />the AI Interview yet.
                                    </>
                                )}
                            </p>
                        </>
                    )}
                </div>
            </div>
            {/* EL audio-only fallback — when the candidate video upload failed there's no
                video to merge with, so the EL conversation audio (both voices) surfaces
                here as a standalone player. There's no video to sync (elAudioStandalone
                requires !videoSrc), so the dual-track sync effect short-circuits and
                audioRef isn't used here — the custom player owns its own element. */}
            {elAudioStandalone && conversationAudioUrl && isReviewableStatus && (
                <div className="mb-4">
                    <AudioRecordingPlayer
                        src={conversationAudioUrl}
                        onDurationChange={_setInterviewDuration}
                    />
                </div>
            )}
            <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {props.videoInterview && props.interviewStatus?.status === interviewStatusAborted ? (
                        `Interview aborted at ${parseFormat(props.videoInterview.created_at || "", "D MMMM YYYY")}`
                    ) : (
                        <>
                            {(props.s3VideoLink || useElAudio || isCombined || isAudioSrc) ?
                                `Recorded on ${parseFormat(props.videoInterview.created_at || "", "D MMMM YYYY")}` :
                                props.interviewStatus?.status === interviewStatusInComplete ? "Incomplete interview" : "Not recorded yet"
                            }
                        </>
                    )}
                </div>
                {props.interviewStatus?.status !== interviewStatusAborted && (props.videoInterview?.s3_link || useElAudio || isAudioSrc) && (
                    <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {interviewDuration !== null ? (
                            interviewDuration < 60
                                ? `${Math.floor(interviewDuration)} seconds`
                                : formatDuration(interviewDuration)
                        ) : "Loading duration..."}
                    </div>
                )}
            </div>
        </div>
    )
}